
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { parseMdsText, analyzeFileContent, cleanJsonString } from './services/geminiService';
import { processMdsData } from './utils/bomProcessor';
import { ext_validateHierarchy } from './services/MdsValidationEngine';
import { ProcessedMds, ValidationResult, MdsItem, Status, Millsheet, DwgData, ItemDetail, ImdsStandard } from './types';
import MdsTable from './components/MdsTable';
import TreeView from './components/TreeView';
import JsonOutput from './components/JsonOutput';
import ImdsStandardPage from './components/ImdsStandardPage';
import SettingsPage from './components/SettingsPage';
import CasComplianceChecker from './components/CasComplianceChecker';
import * as dbService from './services/firebaseService';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';

type ViewState = 'dashboard' | 'archives' | 'terminal' | 'detail' | 'standards' | 'settings' | 'cas-checker';
type ProcessingPhase = 'IDLE' | 'READING' | 'AI_PARSING' | 'VALIDATING' | 'SYNCING' | 'ANALYZING_FILE';

import { parseImdsExcel } from './utils/imdsParser';

const App: React.FC = () => {
  const [view, setView] = useState<ViewState>('dashboard');
  const [phase, setPhase] = useState<ProcessingPhase>('IDLE');
  const [savedFiles, setSavedFiles] = useState<ProcessedMds[]>([]);
  const [standards, setStandards] = useState<ImdsStandard[]>([]);
  const [currentFile, setCurrentFile] = useState<ProcessedMds | null>(null);
  const [activeTab, setActiveTab] = useState<'material' | 'tree' | 'json' | 'compliance'>('material');
  const [uploadTab, setUploadTab] = useState<'excel' | 'paste' | 'universal'>('excel');
  const [fileSearchTerm, setFileSearchTerm] = useState('');
  const [manualText, setManualText] = useState('');
  const [syncStatus, setSyncStatus] = useState<'CLOUD' | 'LOCAL'>('LOCAL');
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('mds_theme') === 'dark');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [analysisResult, setAnalysisResult] = useState<string>('');
  const [pendingUniversalFile, setPendingUniversalFile] = useState<ProcessedMds | null>(null);
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const universalFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('mds_theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('mds_theme', 'light');
    }
  }, [darkMode]);

  const initData = async () => {
    setPhase('SYNCING');
    try {
      const cloudFiles = await dbService.firebase_getFiles();
      const cloudStds = await dbService.firebase_getStandards();
      setSyncStatus(dbService.getSyncStatus());
      setSavedFiles(cloudFiles as ProcessedMds[]);
      setStandards(cloudStds as ImdsStandard[]);
    } catch (e: any) {
      console.error("Initial Sync Error:", e);
    } finally {
      setPhase('IDLE');
    }
  };

  useEffect(() => { initData(); }, []);

  const saveToFiles = useCallback(async (data: ProcessedMds) => {
    if (!data) return;
    setPhase('SYNCING');
    try {
      await dbService.firebase_saveFile(data);
      const updatedFiles = await dbService.firebase_getFiles();
      setSavedFiles(updatedFiles as ProcessedMds[]);
      setSyncStatus(dbService.getSyncStatus());
      setLastSaved(new Date().toLocaleTimeString());
    } catch (e: any) { 
      console.error("Critical Save Error:", e);
      alert("Cloud Save Error: " + (e.message || "Unknown error"));
    } finally { 
      setPhase('IDLE'); 
    }
  }, []);

  const handleUpdateDetails = useCallback(async (partNumber: string, details: ItemDetail) => {
    if (!currentFile) return;
    const newItems = currentFile.items.map(item => 
      item.part_number_customer === partNumber ? { ...item, details: { ...details } } : item
    );
    const { data } = processMdsData(currentFile.dwg, newItems);
    if (data) {
      const updatedFile: ProcessedMds = { ...data, uploadedAt: currentFile.uploadedAt };
      setCurrentFile(updatedFile);
      await saveToFiles(updatedFile);
    }
  }, [currentFile, saveToFiles]);

  const handleUpdateItem = useCallback(async (updatedItem: MdsItem, originalPartNumber?: string) => {
    if (!currentFile) return;
    const newItems = currentFile.items.map(item => 
      (originalPartNumber ? item.part_number_customer === originalPartNumber : item.no === updatedItem.no) 
      ? updatedItem : item
    );
    const { data } = processMdsData(currentFile.dwg, newItems);
    if (data) {
      const updatedFile: ProcessedMds = { ...data, uploadedAt: currentFile.uploadedAt };
      setCurrentFile(updatedFile);
      await saveToFiles(updatedFile);
    }
  }, [currentFile, saveToFiles]);

  const handleAddChild = useCallback(async (newData: Partial<MdsItem>) => {
    if (!currentFile) return;
    const nextNo = Math.max(...currentFile.items.map(i => i.no), 0) + 1;
    const newItem: MdsItem = {
      no: nextNo,
      level: newData.level || 0,
      part_number_customer: newData.part_number_customer || `NEW-${Date.now()}`,
      part_number_idbm: newData.part_number_idbm || '-',
      part_name_customer: newData.part_name_customer || 'UNNAMED COMPONENT',
      part_name_idbm: newData.part_name_idbm || '-',
      qty_r: newData.qty_r || 0,
      qty_l: newData.qty_l || 0,
      qty_total: (newData.qty_r || 0) + (newData.qty_l || 0),
      status: Status.NEW,
      uom: newData.uom || 'PC',
      berat_level: 0,
      berat_total: 0,
      material: newData.material || '-',
      millsheet: Millsheet.X,
      manufacturer: newData.manufacturer || '-',
      parent_part_number: newData.parent_part_number || '',
      details: { pdfs: [], customFields: [], rohsComposition: {} }
    };
    const newItems = [...currentFile.items, newItem];
    const { data } = processMdsData(currentFile.dwg, newItems);
    if (data) {
      const updatedFile: ProcessedMds = { ...data, uploadedAt: currentFile.uploadedAt };
      setCurrentFile(updatedFile);
      await saveToFiles(updatedFile);
    }
  }, [currentFile, saveToFiles]);

  const handleInjectS45CData = useCallback(async () => {
    if (!currentFile) return;
    
    const s45cIndex = currentFile.items.findIndex(i => i.part_number_customer === 'S45C');
    if (s45cIndex === -1) {
        alert("Item 'S45C' not found.");
        return;
    }

    const parentItem = currentFile.items[s45cIndex];
    const newLevel = parentItem.level + 1;

    const nickelItem: MdsItem = {
        no: 0, // Will be re-indexed
        level: newLevel,
        part_number_customer: 'S45C-Ni',
        part_number_idbm: '-',
        part_name_customer: 'Nickel',
        part_name_idbm: '-',
        qty_r: 0.01,
        qty_l: 0,
        qty_total: 0.01,
        status: Status.NEW,
        uom: 'MG',
        berat_level: 0.01,
        berat_total: 0.01,
        material: '-',
        millsheet: Millsheet.X,
        manufacturer: '-',
        parent_part_number: parentItem.part_number_customer,
        imdsData: {
            substanceName: 'Nickel',
            casNo: '7440-02-0',
            quantity: 0.01,
            weight: 0.01,
            portion: 0.01,
            classification: 'D'
        }
    };

    const copperItem: MdsItem = {
        no: 0, // Will be re-indexed
        level: newLevel,
        part_number_customer: 'S45C-Cu',
        part_number_idbm: '-',
        part_name_customer: 'Copper',
        part_name_idbm: '-',
        qty_r: 0.02,
        qty_l: 0,
        qty_total: 0.02,
        status: Status.NEW,
        uom: 'MG',
        berat_level: 0.02,
        berat_total: 0.02,
        material: '-',
        millsheet: Millsheet.X,
        manufacturer: '-',
        parent_part_number: parentItem.part_number_customer,
        imdsData: {
            substanceName: 'Copper',
            casNo: '7440-50-8',
            quantity: 0.02,
            weight: 0.02,
            portion: 0.02,
            classification: 'D / P'
        }
    };

    const newItems = [...currentFile.items];
    newItems.splice(s45cIndex + 1, 0, nickelItem, copperItem);

    const { data } = processMdsData(currentFile.dwg, newItems);
    if (data) {
        const updatedFile: ProcessedMds = { ...data, uploadedAt: currentFile.uploadedAt };
        setCurrentFile(updatedFile);
        await saveToFiles(updatedFile);
        alert("S45C data injected successfully.");
    }
  }, [currentFile, saveToFiles]);

  const handleDeleteItem = useCallback(async (partNumber: string) => {
    if (!currentFile) return;
    if (!window.confirm(`Hapus item ${partNumber} dan seluruh cabangnya?`)) return;
    const newItems = currentFile.items.filter(i => i.part_number_customer !== partNumber);
    const { data } = processMdsData(currentFile.dwg, newItems);
    if (data) {
      const updatedFile: ProcessedMds = { ...data, uploadedAt: currentFile.uploadedAt };
      setCurrentFile(updatedFile);
      await saveToFiles(updatedFile);
    }
  }, [currentFile, saveToFiles]);

  const handleDeleteArchive = useCallback(async (dwgNumber: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Hapus drawing ${dwgNumber} dari arsip lokal?`)) return;
    
    // Optimistic Update: Remove from UI immediately
    setSavedFiles(prev => prev.filter(f => f.dwg.dwg_number !== dwgNumber));
    if (currentFile?.dwg.dwg_number === dwgNumber) setCurrentFile(null);
    
    setPhase('SYNCING');
    try {
      // Attempt to delete from local storage
      const success = await dbService.firebase_deleteFile(dwgNumber);
      
      if (!success) {
        throw new Error("Local delete failed. Item not found or storage error.");
      }
      
      // Force refresh from local storage to ensure UI matches persistence
      await initData();
    } catch (err: any) {
      console.error("Delete failed:", err);
      // If failed, refresh to restore the item
      await initData();
      alert(`Gagal menghapus file: ${err.message || "Unknown error"}`);
    } finally {
      setPhase('IDLE');
    }
  }, [currentFile, initData]);

  const handleClearAllArchives = useCallback(async () => {
    if (!window.confirm("WARNING: This will delete ALL archived files locally. This action cannot be undone. Are you sure?")) return;
    setPhase('SYNCING');
    try {
      await dbService.firebase_deleteAllFiles();
      setSavedFiles([]); // Clear UI immediately
      setCurrentFile(null);
      // Force reload to ensure clean state
      window.location.reload();
    } catch (e) {
      alert("Failed to clear archives.");
    } finally {
      setPhase('IDLE');
    }
  }, [initData]);

  const handleUniversalFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhase('ANALYZING_FILE');
    setAnalysisResult('');

    try {
      let mimeType = file.type;
      let dataToSend = '';

      if (file.name.endsWith('.docx')) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        dataToSend = btoa(result.value);
        mimeType = 'text/plain';
      } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        const arrayBuffer = await file.arrayBuffer();
        const wb = XLSX.read(arrayBuffer);
        const ws = wb.Sheets[wb.SheetNames[0]];
        const csv = XLSX.utils.sheet_to_csv(ws);
        dataToSend = btoa(csv);
        mimeType = 'text/csv';
      } else {
        const reader = new FileReader();
        dataToSend = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }

      const result = await analyzeFileContent(
        { mimeType, data: dataToSend },
        file.name
      );
      setAnalysisResult(result);

      // Auto-save to Archives
      try {
        const jsonStr = cleanJsonString(result);
        const parsedResult = JSON.parse(jsonStr);
        
        const rohsComposition: { [key: string]: string } = {};
        if (parsedResult.substances && Array.isArray(parsedResult.substances)) {
          parsedResult.substances.forEach((sub: any) => {
            // Use CAS if available, otherwise slugify name
            const key = sub.cas ? sub.cas.replace(/[^\d]/g, '') : sub.name.toLowerCase().replace(/\s+/g, '_');
            // Convert percentage to PPM (1% = 10000 PPM)
            const ppm = (parseFloat(sub.percentage) || 0) * 10000;
            rohsComposition[key] = ppm.toString();
          });
        }

        const newItem: MdsItem = {
          no: 1,
          level: 0,
          part_number_customer: parsedResult.metadata?.part_number || file.name,
          part_number_idbm: '-',
          part_name_customer: parsedResult.metadata?.description || parsedResult.metadata?.title || 'Imported Document',
          part_name_idbm: '-',
          qty_r: 1,
          qty_l: 0,
          qty_total: 1,
          status: Status.NEW,
          uom: 'DOC',
          berat_level: parseFloat(parsedResult.metadata?.weight_total) || 0,
          berat_total: parseFloat(parsedResult.metadata?.weight_total) || 0,
          material: '-',
          millsheet: Millsheet.X,
          manufacturer: parsedResult.metadata?.supplier || '-',
          parent_part_number: '',
          imds_number: parsedResult.metadata?.imds_id || '-',
          details: {
            pdfs: [],
            customFields: [
              { id: 'summary', label: 'AI Summary', value: parsedResult.summary || '' },
              { id: 'raw_analysis', label: 'Full Analysis', value: JSON.stringify(parsedResult, null, 2) }
            ],
            rohsComposition
          }
        };

        // Create child items from IMDS Tree if available (Priority over tables)
        const childItems: MdsItem[] = [];
        if (parsedResult.imds_tree && Array.isArray(parsedResult.imds_tree)) {
          parsedResult.imds_tree.forEach((node: any, index: number) => {
            // Skip root node if it duplicates the main item
            if (node.tree_level === 0 || node.tree_level === 1 && index === 0) return;

            const compName = node.description || node.article_name || node.name || node.substance_name || `Item ${index}`;
            const material = node.material_no || node.item_mat_no || '-';
            const weight = parseFloat(node.weight) || 0;
            
            // Map substances for RoHS composition
            const compRohs: { [key: string]: string } = {};
            if (node.substance_name && node.portion) {
               const key = node.cas_no ? node.cas_no.replace(/[^\d]/g, '') : node.substance_name.toLowerCase().replace(/\s+/g, '_');
               const ppm = (parseFloat(node.portion) || 0) * 10000;
               compRohs[key] = ppm.toString();
            }

            childItems.push({
              no: index + 2,
              level: node.tree_level || 1,
              part_number_customer: node.part_item_no || node.item_mat_no || `${parsedResult.metadata?.part_number}-${index}`,
              part_number_idbm: '-',
              part_name_customer: compName,
              part_name_idbm: '-',
              qty_r: node.quantity || 1,
              qty_l: 0,
              qty_total: node.quantity || 1,
              status: Status.NEW,
              uom: node.substance_name ? 'MG' : 'PC', // Substances usually in weight, parts in PC
              berat_level: weight,
              berat_total: weight,
              material: material,
              millsheet: Millsheet.X,
              manufacturer: '-',
              parent_part_number: parsedResult.metadata?.part_number || file.name,
              imds_number: node.imds_id_version,
              imdsData: {
                treeLevel: node.tree_level,
                description: node.description,
                articleName: node.article_name,
                name: node.name,
                substanceName: node.substance_name,
                partItemNo: node.part_item_no,
                itemMatNo: node.item_mat_no,
                materialNo: node.material_no,
                casNo: node.cas_no,
                imdsIdVersion: node.imds_id_version,
                quantity: node.quantity,
                weight: node.weight,
                portion: node.portion,
                portionRange: node.portion_range,
                classification: node.classification,
                gadslSvhc: node.gadsl_svhc,
                partsMarking: node.parts_marking,
                sourceOfMaterial: node.source_of_material,
                applicationId: node.application_id,
                recyclate: node.recyclate
              },
              details: {
                pdfs: [],
                customFields: [],
                rohsComposition: compRohs
              }
            });
          });
        } else if (parsedResult.tables && Array.isArray(parsedResult.tables)) {
          // Fallback to table parsing if no IMDS tree found
          parsedResult.tables.forEach((table: any) => {
            if (table.rows && Array.isArray(table.rows)) {
              table.rows.forEach((row: any, index: number) => {
                // ... existing table parsing logic ...
                 // Try to find component name and material from various possible keys
                const compName = row.component || row.part_name || row.name || `Component ${index + 1}`;
                const material = row.material || row.substance || '-';
                const weightStr = row.weight || row.mass || '0';
                const weight = parseFloat(weightStr.toString().replace(/[^\d.]/g, '')) || 0;

                // Filter substances relevant to this component if source_material matches
                const compRohs: { [key: string]: string } = {};
                if (parsedResult.substances && Array.isArray(parsedResult.substances)) {
                  parsedResult.substances.forEach((sub: any) => {
                    // Check if substance is linked to this component's material
                    if (sub.source_material && material.includes(sub.source_material)) {
                       const key = sub.cas ? sub.cas.replace(/[^\d]/g, '') : sub.name.toLowerCase().replace(/\s+/g, '_');
                       const ppm = (parseFloat(sub.percentage) || 0) * 10000;
                       compRohs[key] = ppm.toString();
                    }
                  });
                }

                childItems.push({
                  no: index + 2, // Start from 2
                  level: 1,
                  part_number_customer: `${parsedResult.metadata?.part_number || file.name}-${index + 1}`,
                  part_number_idbm: '-',
                  part_name_customer: compName,
                  part_name_idbm: '-',
                  qty_r: 1,
                  qty_l: 0,
                  qty_total: 1,
                  status: Status.NEW,
                  uom: 'PC',
                  berat_level: weight,
                  berat_total: weight,
                  material: material,
                  millsheet: Millsheet.X,
                  manufacturer: '-',
                  parent_part_number: parsedResult.metadata?.part_number || file.name,
                  details: {
                    pdfs: [],
                    customFields: [],
                    rohsComposition: compRohs
                  }
                });
              });
            }
          });
        }

        const allItems = [newItem, ...childItems];

        const { data } = processMdsData({
            customer: parsedResult.metadata?.recipient || 'Universal Analysis',
            dwg_number: parsedResult.metadata?.part_number || file.name,
            hr_mark: '-',
            status: Status.NEW
        }, allItems);

        if (data) {
             const validation = ext_validateHierarchy(data.items);
             if (!validation.isValid) {
                 alert("Hierarchy Validation Failed:\n" + validation.errors.join("\n"));
             }
             const newFile: ProcessedMds = { ...data, uploadedAt: new Date().toISOString() };
             setPendingUniversalFile(newFile);
             console.log("Analysis ready to be saved.");
        }

      } catch (e) {
        console.error("Failed to parse or save analysis result:", e);
      }

    } catch (error: any) {
      console.error("Analysis Error:", error);
      alert("Gagal menganalisis file: " + (error.message || "Unknown error"));
    } finally {
      setPhase('IDLE');
      if (universalFileInputRef.current) universalFileInputRef.current.value = '';
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhase('READING');
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const bstr: any = reader.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];

        // Try deterministic parsing first
        const deterministicResult = parseImdsExcel(data, file.name);
        if (deterministicResult) {
            setPhase('VALIDATING');
            setCurrentFile(deterministicResult);
            await saveToFiles(deterministicResult);
            setView('detail');
            setPhase('IDLE');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        // Fallback to AI parsing
        const text = data.map(row => (Array.isArray(row) ? row.join('|') : '')).join('\n');
        await handleProcess(text);
      } catch (error: any) {
        alert("Gagal membaca file Excel.");
      } finally {
        setPhase('IDLE');
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleProcess = async (textToProcess: string) => {
    if (!textToProcess.trim()) return;
    setPhase('AI_PARSING');
    try {
      const parsed = await parseMdsText(textToProcess);
      setPhase('VALIDATING');
      const { data } = processMdsData(parsed.dwg, parsed.items);
      if (data) {
        const validation = ext_validateHierarchy(data.items);
        if (!validation.isValid) {
          alert("Hierarchy Validation Failed:\n" + validation.errors.join("\n"));
        }
        const fileWithMeta: ProcessedMds = { ...data, uploadedAt: new Date().toISOString() };
        setCurrentFile(fileWithMeta);
        await saveToFiles(fileWithMeta);
        setView('detail');
        setManualText(''); 
      }
    } catch (error: any) { 
      alert("Gagal memproses data AI.");
    } finally { 
      setPhase('IDLE'); 
    }
  };

  const dashboardStats = useMemo(() => {
    const totalDwg = savedFiles.length;
    const allItems = savedFiles.flatMap(f => f.items);
    const totalWeight = savedFiles.reduce((acc, f) => acc + f.summary.totalWeight, 0);
    const recentFiles = [...savedFiles].sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()).slice(0, 8);
    
    // Detailed Risks List
    const risks: any[] = [];
    savedFiles.forEach(file => {
      file.items.forEach(item => {
        if (item.details?.rohsComposition) {
          Object.entries(item.details.rohsComposition).forEach(([sub, val]) => {
            const actual = parseFloat(val || '0');
            const limit = item.details?.rohsLimits?.[sub] ?? 1000;
            if (actual > limit) {
              risks.push({
                dwg: file.dwg.dwg_number,
                part: item.part_number_customer,
                substance: sub.toUpperCase(),
                actual,
                limit,
                customer: file.dwg.customer
              });
            }
          });
        }
      });
    });

    return { totalDwg, totalItems: allItems.length, totalWeight, recentFiles, risks };
  }, [savedFiles]);

  const navigateToRisk = useCallback((dwgNo: string) => {
    const targetFile = savedFiles.find(f => f.dwg.dwg_number === dwgNo);
    if (targetFile) {
      setCurrentFile(targetFile);
      setView('detail');
      setActiveTab('material');
    }
  }, [savedFiles]);

  const filteredArchives = useMemo(() => {
    const term = fileSearchTerm.toLowerCase().trim();
    return savedFiles.filter(f => 
      f.dwg.dwg_number.toLowerCase().includes(term) ||
      f.dwg.customer.toLowerCase().includes(term) ||
      f.items.some(i => 
        (i.imdsData?.casNo && i.imdsData.casNo.toLowerCase().includes(term)) ||
        (i.imdsData?.substanceName && i.imdsData.substanceName.toLowerCase().includes(term))
      )
    );
  }, [savedFiles, fileSearchTerm]);

  return (
    <div className="min-h-screen flex font-sans bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      {/* Loading Overlay */}
      {phase !== 'IDLE' && (
        <div className="fixed inset-0 bg-white/80 dark:bg-black/80 backdrop-blur-sm z-[100] flex flex-col items-center justify-center">
          <div className="w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-4"></div>
          <h3 className="text-lg font-medium text-slate-900 dark:text-white">{phase.replace('_', ' ')}...</h3>
          <p className="text-sm text-slate-500 mt-1">Synchronizing data</p>
        </div>
      )}

      {/* Sidebar */}
      <aside className="w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 flex flex-col z-50 shrink-0">
        <div className="p-6 border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">M</div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-900 dark:text-white block leading-none">MDS Cloud</span>
                <span className="px-1.5 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[10px] font-bold leading-none">v1.1.0</span>
              </div>
              <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mt-1 block">Enterprise Edition</span>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
            )},
            { id: 'archives', label: 'Data Archives', icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
            )},
            { id: 'terminal', label: 'Import Data', icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
            )},
            { id: 'cas-checker', label: 'CAS Checker', icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
            )},
            { id: 'standards', label: 'Compliance', icon: (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            )},
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setView(item.id as ViewState)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                view === item.id 
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' 
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-100 dark:border-slate-700">
          <button 
            onClick={() => setView('settings')}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              view === 'settings' 
                ? 'bg-slate-100 text-slate-900 dark:bg-slate-700 dark:text-white' 
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
            }`}
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              <span>Settings</span>
            </div>
            <div className={`w-2 h-2 rounded-full ${syncStatus === 'CLOUD' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto h-screen">
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
              {view === 'dashboard' && 'Overview'}
              {view === 'archives' && 'Data Archives'}
              {view === 'terminal' && 'Import & Parse'}
              {view === 'detail' && (currentFile ? `Project: ${currentFile.dwg.dwg_number}` : 'Project Details')}
              {view === 'standards' && 'Compliance Standards'}
              {view === 'settings' && 'System Settings'}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {view === 'detail' && currentFile ? currentFile.dwg.customer : `Last synced: ${lastSaved || 'Just now'}`}
            </p>
          </div>
          <div className="flex items-center gap-4">
             <div className="text-right hidden md:block">
               <div className="text-sm font-medium text-slate-900 dark:text-white">{currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
               <div className="text-xs text-slate-500">{currentTime.toLocaleDateString()}</div>
             </div>
             <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-400">
                <span className="text-xs font-bold">JD</span>
             </div>
          </div>
        </header>

        <div className="p-8 max-w-7xl mx-auto">
          {view === 'dashboard' && (
            <div className="space-y-8">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                      <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Total Projects</div>
                      <div className="text-3xl font-bold text-slate-900 dark:text-white">{dashboardStats.totalDwg}</div>
                      <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                        <span>Active</span>
                      </div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                      <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Compliance Risks</div>
                      <div className={`text-3xl font-bold ${dashboardStats.risks.length > 0 ? 'text-red-600' : 'text-slate-900 dark:text-white'}`}>{dashboardStats.risks.length}</div>
                      <div className="mt-2 text-xs text-slate-500">Requires attention</div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                      <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Total Weight</div>
                      <div className="text-3xl font-bold text-slate-900 dark:text-white">{(dashboardStats.totalWeight / 1000).toFixed(2)}<span className="text-lg font-normal text-slate-500 ml-1">kg</span></div>
                      <div className="mt-2 text-xs text-slate-500">Across all projects</div>
                  </div>
                  <div className="bg-blue-600 p-6 rounded-xl border border-blue-500 shadow-sm flex flex-col justify-between text-white">
                      <div>
                        <div className="text-xs font-medium text-blue-100 uppercase tracking-wider mb-2">Quick Action</div>
                        <div className="text-lg font-bold">Sync Cloud Data</div>
                      </div>
                      <button onClick={initData} className="mt-4 w-full bg-white/10 hover:bg-white/20 text-white py-2 rounded-lg text-xs font-bold uppercase transition-colors">Refresh Now</button>
                  </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Recent Activity */}
                  <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                     <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
                       <h3 className="font-semibold text-slate-900 dark:text-white">Recent Projects</h3>
                       <button onClick={() => setView('archives')} className="text-xs font-medium text-blue-600 hover:text-blue-700">View All</button>
                     </div>
                     <div className="divide-y divide-slate-100 dark:divide-slate-700">
                        {dashboardStats.recentFiles.map(file => (
                          <div 
                            key={file.dwg.dwg_number} 
                            className="flex justify-between items-center p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer group" 
                            onClick={() => { setCurrentFile(file); setView('detail'); }}
                          >
                            <div className="flex items-center gap-4">
                               <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-500">
                                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                               </div>
                               <div>
                                  <div className="font-medium text-sm text-slate-900 dark:text-white group-hover:text-blue-600 transition-colors">{file.dwg.dwg_number}</div>
                                  <div className="text-xs text-slate-500">{file.dwg.customer}</div>
                               </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs font-medium text-slate-900 dark:text-white">{new Date(file.uploadedAt).toLocaleDateString()}</div>
                              <div className="text-[10px] text-slate-400 uppercase">{file.items.length} Items</div>
                            </div>
                          </div>
                        ))}
                     </div>
                  </div>

                  {/* Risk Monitor */}
                  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col">
                     <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-red-50/50 dark:bg-red-900/10">
                       <h3 className="font-semibold text-red-700 dark:text-red-400">Compliance Risks</h3>
                       {dashboardStats.risks.length > 0 && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">{dashboardStats.risks.length} Issues</span>}
                     </div>
                     <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[400px]">
                        {dashboardStats.risks.length === 0 ? (
                          <div className="h-40 flex flex-col items-center justify-center text-slate-400 text-center">
                             <svg className="w-12 h-12 mb-2 text-green-500 opacity-20" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                             <p className="text-sm font-medium text-slate-500">All systems compliant</p>
                          </div>
                        ) : (
                          dashboardStats.risks.map((risk, idx) => (
                            <div 
                              key={idx} 
                              onClick={() => navigateToRisk(risk.dwg)}
                              className="p-3 rounded-lg border border-red-100 bg-red-50/30 hover:bg-red-50 cursor-pointer transition-colors group"
                            >
                               <div className="flex justify-between items-start mb-1">
                                  <span className="text-xs font-bold text-red-600 uppercase">{risk.substance}</span>
                                  <span className="text-[10px] font-medium text-red-500">{risk.actual} PPM</span>
                               </div>
                               <div className="text-xs font-medium text-slate-700">{risk.part}</div>
                               <div className="text-[10px] text-slate-400 mt-1">{risk.dwg}</div>
                            </div>
                          ))
                        )}
                     </div>
                  </div>
              </div>
            </div>
          )}

          {view === 'archives' && (
            <div className="space-y-6">
               <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex gap-4">
                  <div className="flex-1 relative">
                    <svg className="w-5 h-5 absolute left-3 top-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    <input 
                      type="text" 
                      placeholder="Search archives..." 
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border-none rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none dark:text-white" 
                      value={fileSearchTerm} 
                      onChange={e => setFileSearchTerm(e.target.value)} 
                    />
                  </div>
                  <button 
                    onClick={handleClearAllArchives}
                    className="px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    Clear All
                  </button>
               </div>
               
               <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700 text-xs uppercase text-slate-500 font-medium">
                          <tr>
                            <th className="px-6 py-4">Customer</th>
                            <th className="px-6 py-4">Drawing No</th>
                            <th className="px-6 py-4">Type</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4">Last Modified</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                          {filteredArchives.map(file => (
                            <tr key={file.dwg.dwg_number} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors group">
                              <td className="px-6 py-4 font-medium text-slate-900 dark:text-white cursor-pointer" onClick={() => { setCurrentFile(file); setView('detail'); }}>{file.dwg.customer}</td>
                              <td className="px-6 py-4 text-slate-600 dark:text-slate-300 cursor-pointer" onClick={() => { setCurrentFile(file); setView('detail'); }}>{file.dwg.dwg_number}</td>
                              <td className="px-6 py-4 cursor-pointer" onClick={() => { setCurrentFile(file); setView('detail'); }}>
                                {file.items.some(i => i.uom === 'ROHS_DOC') ? (
                                  <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 text-[10px] font-bold">ROHS</span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-[10px] font-bold">DRAWING</span>
                                )}
                              </td>
                              <td className="px-6 py-4 cursor-pointer" onClick={() => { setCurrentFile(file); setView('detail'); }}>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${file.dwg.status === Status.NEW ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                  {file.dwg.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-slate-500 text-xs cursor-pointer" onClick={() => { setCurrentFile(file); setView('detail'); }}>
                                {new Date(file.uploadedAt).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 text-right">
                                 <button onClick={(e) => handleDeleteArchive(file.dwg.dwg_number, e)} className="text-slate-400 hover:text-red-600 transition-colors p-2 rounded-lg hover:bg-red-50">
                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                 </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
               </div>
            </div>
          )}

          {view === 'terminal' && (
             <div className="max-w-4xl mx-auto space-y-8">
               <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                  <div className="flex border-b border-slate-200 dark:border-slate-700">
                    <button onClick={() => setUploadTab('excel')} className={`flex-1 py-4 text-sm font-medium transition-colors ${uploadTab === 'excel' ? 'bg-white dark:bg-slate-800 text-blue-600 border-b-2 border-blue-600' : 'bg-slate-50 dark:bg-slate-900 text-slate-500'}`}>Excel Parser</button>
                    <button onClick={() => setUploadTab('paste')} className={`flex-1 py-4 text-sm font-medium transition-colors ${uploadTab === 'paste' ? 'bg-white dark:bg-slate-800 text-blue-600 border-b-2 border-blue-600' : 'bg-slate-50 dark:bg-slate-900 text-slate-500'}`}>Manual Input</button>
                    <button onClick={() => setUploadTab('universal')} className={`flex-1 py-4 text-sm font-medium transition-colors ${uploadTab === 'universal' ? 'bg-white dark:bg-slate-800 text-blue-600 border-b-2 border-blue-600' : 'bg-slate-50 dark:bg-slate-900 text-slate-500'}`}>Universal Analysis</button>
                  </div>
                  <div className="p-8">
                    {uploadTab === 'excel' && (
                      <div onClick={() => fileInputRef.current?.click()} className="h-64 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-slate-700/50 transition-all group">
                          <input type="file" ref={fileInputRef} className="hidden" accept=".xlsx, .xls, .csv" onChange={handleFileUpload} />
                          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                            <svg className="w-8 h-8 text-slate-400 group-hover:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                          </div>
                          <h4 className="text-lg font-semibold text-slate-900 dark:text-white">Upload Excel File</h4>
                          <p className="text-sm text-slate-500 mt-2">Drag and drop or click to browse</p>
                      </div>
                    )}
                    {uploadTab === 'paste' && (
                      <div className="space-y-4">
                        <textarea className="w-full p-4 h-64 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none" placeholder="Paste BOM text here..." value={manualText} onChange={e => setManualText(e.target.value)} />
                        <button onClick={() => handleProcess(manualText)} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium shadow-sm transition-colors">Process Data</button>
                      </div>
                    )}
                    {uploadTab === 'universal' && (
                      <div className="space-y-6">
                        <div onClick={() => universalFileInputRef.current?.click()} className="h-48 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-slate-700/50 transition-all group">
                            <input type="file" ref={universalFileInputRef} className="hidden" onChange={handleUniversalFileUpload} />
                            <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                              <svg className="w-6 h-6 text-slate-400 group-hover:text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            </div>
                            <h4 className="text-base font-semibold text-slate-900 dark:text-white">Universal File Analysis</h4>
                            <p className="text-xs text-slate-500 mt-1">Support PDF, Excel, Word, Text</p>
                        </div>
                        
                        {analysisResult && (
                          <div className="bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                                AI Analysis Result
                              </h3>
                              {pendingUniversalFile && (
                                <button 
                                  onClick={async () => {
                                    await saveToFiles(pendingUniversalFile);
                                    setPendingUniversalFile(null);
                                    alert("Analysis saved to Data Archives successfully!");
                                  }} 
                                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 shadow-sm transition-colors flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                                  Save to Archives
                                </button>
                              )}
                            </div>
                            <div className="prose dark:prose-invert max-w-none text-sm">
                              <pre className="whitespace-pre-wrap font-mono bg-white dark:bg-black/30 p-4 rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto">
                                {analysisResult}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
               </div>
            </div>
          )}

          {view === 'detail' && currentFile && (
            <div className="space-y-6">
               <div className="flex justify-between items-center">
                  <button onClick={() => setView('archives')} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    Back to Archives
                  </button>
                  <div className="flex gap-3">
                    {currentFile.items.some(i => i.part_number_customer === 'S45C') && (
                        <button onClick={handleInjectS45CData} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 shadow-sm transition-colors flex items-center gap-2">
                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                           Inject S45C Data
                        </button>
                    )}
                    <button onClick={() => saveToFiles(currentFile)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm transition-colors flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                      Save Changes
                    </button>
                  </div>
               </div>
               
               <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden min-h-[600px]">
                  <div className="flex border-b border-slate-200 dark:border-slate-700 px-6">
                     {['material', 'tree', 'json', 'compliance'].map(tab => (
                       <button 
                         key={tab} 
                         onClick={() => setActiveTab(tab as any)} 
                         className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                       >
                         {tab === 'tree' ? 'Hierarchy Tree' : tab === 'material' ? 'Material Data' : tab === 'json' ? 'Raw JSON' : 'Compliance'}
                       </button>
                     ))}
                  </div>
                  <div className="p-6 bg-slate-50/50 dark:bg-slate-900/50 min-h-[600px]">
                     {activeTab === 'material' && <MdsTable items={currentFile.items} onUpdateItem={handleUpdateItem} onDeleteItem={handleDeleteItem} onUpdateDetails={handleUpdateDetails} initialSearchTerm={globalSearchTerm} />}
                     {activeTab === 'tree' && <TreeView tree={currentFile.tree} onAddChild={handleAddChild} onDelete={handleDeleteItem} onEdit={() => {}} onUpdateDetails={handleUpdateDetails} />}
                     {activeTab === 'json' && <JsonOutput data={currentFile} />}
                     {activeTab === 'compliance' && (
                        <div className="flex flex-col items-center justify-center h-96 text-slate-400">
                          <svg className="w-16 h-16 mb-4 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          <p className="text-lg font-medium">Compliance Module</p>
                          <p className="text-sm">Regulatory analysis tools will appear here.</p>
                        </div>
                     )}
                  </div>
               </div>
            </div>
          )}

          {view === 'standards' && (
            <ImdsStandardPage 
               savedFiles={savedFiles} 
               standards={standards} 
               onSaveStandards={async (newStandards) => { 
                setPhase('SYNCING');
                try { 
                  for(const s of newStandards) { 
                    await dbService.firebase_saveStandard(s); 
                  } 
                  setStandards(newStandards); 
                } finally { 
                  setPhase('IDLE'); 
                }
              }} 
            />
          )}

          {view === 'settings' && (
            <SettingsPage 
              syncStatus={syncStatus} 
              onResetCache={() => { localStorage.removeItem('mds_files_v2'); localStorage.removeItem('mds_standards_v2'); window.location.reload(); }} 
              onClearStandards={() => { localStorage.removeItem('mds_standards_v2'); setStandards([]); alert('Standards cleared.'); }}
              darkMode={darkMode} 
              setDarkMode={setDarkMode} 
              onForceSync={initData} 
            />
          )}

          {view === 'cas-checker' && (
            <CasComplianceChecker 
              savedFiles={savedFiles} 
              standards={standards}
              onViewDrawing={(dwgNumber, partNumber) => {
                const target = savedFiles.find(f => f.dwg.dwg_number === dwgNumber);
                if (target) {
                  setCurrentFile(target);
                  if (partNumber) {
                    setGlobalSearchTerm(partNumber);
                  }
                  setView('detail');
                } else {
                  alert(`Drawing ${dwgNumber} not found in archives.`);
                }
              }}
              onSaveFile={saveToFiles}
              onDeleteFile={async (dwg) => {
                await handleDeleteArchive(dwg);
              }}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default App;
